import type { SharingPlaceOpeningHours } from 'app/types/sharing-place';

export const PLACE_DAY_KEYS = ['day1', 'day2', 'day3', 'day4', 'day5', 'day6', 'day7'] as const;
export type PlaceDayKey = (typeof PLACE_DAY_KEYS)[number];
export const PLACE_DAY_ORDER: PlaceDayKey[] = [...PLACE_DAY_KEYS];

const TIME_PATTERN = /^(\d{1,2}):(\d{2})$/;

export function getPlaceDayKey(date = new Date()): PlaceDayKey {
  return PLACE_DAY_KEYS[(date.getDay() + 6) % 7]!;
}

function parseTimeToMinutes(time: string): number | null {
  const match = TIME_PATTERN.exec(time.trim());
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);

  if (hours > 23 || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
}

function getNowMinutes(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

export function parseOpeningHoursRange(hours: string): {
  openingTime: string;
  closingTime: string;
  openMinutes: number;
  closeMinutes: number;
} | null {
  const [openingPart, closingPart] = hours.split('-');
  if (!openingPart || !closingPart) {
    return null;
  }

  const openingTime = openingPart.trim();
  const closingTime = closingPart.trim();
  const openMinutes = parseTimeToMinutes(openingTime);
  const closeMinutes = parseTimeToMinutes(closingTime);

  if (openMinutes === null || closeMinutes === null || openMinutes >= closeMinutes) {
    return null;
  }

  return { openingTime, closingTime, openMinutes, closeMinutes };
}

export function isWithinOpeningHours(hours: string, now = new Date()): boolean {
  const range = parseOpeningHoursRange(hours);
  if (!range) {
    return false;
  }

  const nowMinutes = getNowMinutes(now);
  return nowMinutes >= range.openMinutes && nowMinutes < range.closeMinutes;
}

export function getNextOpenDayKey(
  openingHours: SharingPlaceOpeningHours | undefined,
  now = new Date(),
): PlaceDayKey | undefined {
  if (!openingHours) {
    return undefined;
  }

  const todayJsDay = now.getDay();
  for (let i = 1; i <= 7; i++) {
    const nextJsDay = (todayJsDay + i) % 7;
    const nextDayKey = PLACE_DAY_KEYS[(nextJsDay + 6) % 7]!;
    const hours = openingHours[nextDayKey];

    if (hours !== undefined && hours !== '') {
      return nextDayKey;
    }
  }

  return undefined;
}

export interface TodayOpeningStatus {
  todayHours?: string;
  isOpen: boolean;
  openingTime?: string;
  closingTime?: string;
  isBeforeOpening: boolean;
  isAfterClosing: boolean;
}

export function getTodayOpeningStatus(
  openingHours: SharingPlaceOpeningHours | undefined,
  now = new Date(),
): TodayOpeningStatus {
  const todayKey = getPlaceDayKey(now);
  const todayHours = openingHours?.[todayKey];

  if (!todayHours) {
    return { isOpen: false, isBeforeOpening: false, isAfterClosing: false };
  }

  const range = parseOpeningHoursRange(todayHours);
  if (!range) {
    return { todayHours, isOpen: false, isBeforeOpening: false, isAfterClosing: false };
  }

  const nowMinutes = getNowMinutes(now);
  const isBeforeOpening = nowMinutes < range.openMinutes;
  const isAfterClosing = nowMinutes >= range.closeMinutes;

  return {
    todayHours,
    isOpen: !isBeforeOpening && !isAfterClosing,
    openingTime: range.openingTime,
    closingTime: range.closingTime,
    isBeforeOpening,
    isAfterClosing,
  };
}
